import React, { useEffect, useState } from 'react';
import { App, Button, Card, Form, Input, Select, Space, Spin } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { recruitErrorMessage } from './shared/recruitUi';

const APPLIED_VIA = ['Manual', 'Referral', 'Agency', 'Walk-in', 'Email', 'Internal'];

const ApplicationFormPage: React.FC = () => {
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [candSearch, setCandSearch] = useState('');

  const presetCandidate = sp.get('candidate_id') || undefined;
  const presetJob = sp.get('job_posting_id') || undefined;

  const { data: jobs } = useQuery({
    queryKey: hrmsKeys.jobs({ picker: 'open' }),
    queryFn: () => hrmsService.pageRecruitJobs({ page: 1, limit: 200, status: 'Published' }),
  });
  const { data: candidates, isFetching } = useQuery({
    queryKey: hrmsKeys.candidates({ picker: candSearch }),
    queryFn: () => hrmsService.pageCandidates({ page: 1, limit: 20, q: candSearch || undefined, blacklisted: 'false' }),
  });
  const { data: preset } = useQuery({
    queryKey: hrmsKeys.candidate(presetCandidate || ''),
    queryFn: () => hrmsService.getCandidate(presetCandidate!),
    enabled: Boolean(presetCandidate),
  });

  useEffect(() => {
    form.setFieldsValue({ candidate_id: presetCandidate, job_posting_id: presetJob });
  }, [presetCandidate, presetJob, form]);

  const candOptions = [
    ...(preset ? [{ value: preset.id, label: `${preset.candidate_number} — ${preset.full_name}` }] : []),
    ...(candidates?.rows || [])
      .filter((c: any) => c.id !== preset?.id)
      .map((c: any) => ({ value: c.id, label: `${c.candidate_number} — ${c.full_name}${c.email ? ` (${c.email})` : ''}` })),
  ];

  const save = async () => {
    try {
      const v = await form.validateFields();
      setSaving(true);
      const created = await hrmsService.createApplication(v);
      message.success(`Application ${created.application_number} created`);
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.applications() });
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.applicationBoard() });
      navigate(`/hr/applications/${created.id}`);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(recruitErrorMessage(e, 'Create failed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="New application"
          subtitle="Put an existing candidate into a job's pipeline (referral, agency, walk-in)"
          primaryActions={
            <Space>
              <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
                Cancel
              </Button>
              <Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={save}>
                Save
              </Button>
            </Space>
          }
        />
      }
    >
      <Card title="Application" style={{ maxWidth: 720 }}>
        <Form form={form} layout="vertical" initialValues={{ applied_via: 'Manual' }}>
          <Form.Item name="job_posting_id" label="Job" rules={[{ required: true, message: 'Select a published job' }]}>
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Published jobs"
              options={(jobs?.rows || []).map((j: any) => ({ value: j.id, label: `${j.job_number} — ${j.title}` }))}
            />
          </Form.Item>
          <Form.Item
            name="candidate_id"
            label="Candidate"
            rules={[{ required: true, message: 'Select a candidate' }]}
            extra={
              <Button type="link" size="small" icon={<PlusOutlined />} style={{ padding: 0 }} onClick={() => navigate('/hr/candidates/new')}>
                New candidate
              </Button>
            }
          >
            <Select
              showSearch
              filterOption={false}
              onSearch={setCandSearch}
              notFoundContent={isFetching ? <Spin size="small" /> : 'No match'}
              placeholder="Search by name, email or phone"
              options={candOptions}
            />
          </Form.Item>
          <Form.Item name="applied_via" label="Applied via">
            <Select options={APPLIED_VIA.map((v) => ({ value: v, label: v }))} />
          </Form.Item>
          <Form.Item name="owner_employee_id" label="Owner (defaults to the job's recruiter)">
            <RecruitPeoplePicker />
          </Form.Item>
          <Form.Item name="cover_letter" label="Cover letter / notes">
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Card>
    </WorkspaceLayout>
  );
};

export default ApplicationFormPage;
